/**
 * Global axios resilience layer (v2 — retry + persistent cache fallback).
 *
 * Why this exists: in production, when the FastAPI backend has a brief
 * cold-start or cluster blip, components that fetch data (homepage book list,
 * trending, featured authors, analytics, etc.) fail their request and render
 * empty states ("No books uploaded yet"). Reopening the browser starts a
 * fresh attempt which usually succeeds, hence the "I have to refresh until
 * it appears" symptom users reported.
 *
 * Two-layer fix:
 *
 *   Layer 1 — RETRY: every idempotent read (GET/HEAD) is auto-retried up to
 *   3 times on network errors and 502/503/504, with exponential backoff
 *   (700ms → 1500ms → 3000ms + jitter). Writes (POST/PUT/DELETE/PATCH)
 *   are never silently retried.
 *
 *   Layer 2 — STALE-WHILE-REVALIDATE CACHE: every successful GET response
 *   for a CACHEABLE_PATH is persisted to localStorage. On subsequent failures
 *   (network/timeout/5xx) we resolve the request from the cache instead of
 *   rejecting, so components keep showing the last-known-good data. Cache
 *   has a 24-hour TTL — older entries are ignored. The cache is
 *   user-scoped via a token-derived key so admin and member views never
 *   leak between accounts.
 *
 * Per-request opt-out: axios.get(url, { _noRetry: true })
 */
import axios from 'axios';

/**
 * Nuke every locally-persisted cache the browser hangs onto so the next
 * page load truly fetches a fresh index.html + fingerprinted assets.
 *
 * Called from two paths:
 *   1. Force-logout: when the axios interceptor sees a
 *      `session_invalidated` 401, we wipe local state + service worker
 *      caches so the user can't accidentally reuse stale JS after an
 *      admin ends all sessions.
 *   2. (Reserved for future use — on version-bump detection.)
 */
export async function bustAppCache() {
  try {
    // 1. Auth token + cached user
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userName');
    localStorage.removeItem('user');
    // 2. Our per-user LS cache namespaces (see writeCache below).
    //    Only remove keys that look like our own; leave unrelated ones alone.
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith('scb:cache:') || k.startsWith('scb:auth:')) {
        localStorage.removeItem(k);
      }
    }
  } catch { /* ignore quota / disabled-storage errors */ }
  try {
    sessionStorage.clear();
  } catch { /* ignore */ }
  try {
    // 3. Service workers — unregister so they can't serve stale JS.
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch { /* ignore */ }
  try {
    // 4. CacheStorage — mostly used by the SW above but might be
    //    populated by any 3rd-party integration.
    if (typeof caches !== 'undefined' && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* ignore */ }
}

// Reasonable default — long enough to cover a moderate cold-start without
// sitting on a screen forever. Tuned down from 25s to 12s after user reports
// of UI lag during cold-starts: now failed requests fall back to cache much
// faster, keeping the app snappy even when the backend is sluggish.
axios.defaults.timeout = 12_000;

// =================== RETRY ===================
const RETRYABLE_METHODS = new Set(['get', 'head', 'options']);
const RETRYABLE_STATUS = new Set([502, 503, 504]);
const MAX_RETRIES = 3;
// Shorter backoff (300/700/1500 + jitter) — total worst-case wait
// before falling back to cache is now ~2.5s instead of >5s.
const BACKOFF_MS = [300, 700, 1500];

const isRetryable = (config, err) => {
  if (!config || config._noRetry) return false;
  const method = (config.method || 'get').toLowerCase();
  if (!RETRYABLE_METHODS.has(method)) return false;
  if (!err.response) return true;
  return RETRYABLE_STATUS.has(err.response.status);
};

// =================== CACHE ===================
const CACHE_VERSION = 'v1';
const CACHE_PREFIX = `scb:cache:${CACHE_VERSION}:`;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const CACHE_MAX_BYTES = 250_000;          // skip caching responses > ~250 KB

// Whitelist of URL substrings whose successful responses are persisted to
// localStorage. We deliberately avoid auth, admin mutations, and anything
// privacy-sensitive; only public/semi-public reads that drive empty-state
// disappearance bugs are included.
const CACHEABLE_PATHS = [
  '/api/books',                       // also catches /api/books/{id}, /api/books?...
  '/api/featured-book',
  '/api/featured-trailers',
  '/api/featured-authors',
  '/api/trending-books',
  '/api/ai-collection',
  '/api/reviews/recent',
  '/api/recommendations',
  '/api/book-views-summary',
  '/api/user/continue-reading',
  '/api/settings/membership',
  '/api/notifications',
  '/api/admin/analytics',
  '/api/admin/royalty-report',
  '/api/books-with-trailers',
];

const userScopeKey = () => {
  // Token-derived but NOT the token itself — just a short stable hash so
  // the cache for a logged-in admin doesn't leak into a member's session
  // on the same browser.
  try {
    const t = localStorage.getItem('token') || 'anon';
    let h = 0;
    for (let i = 0; i < t.length; i += 1) {
      h = ((h << 5) - h + t.charCodeAt(i)) | 0;
    }
    return Math.abs(h).toString(36);
  } catch {
    return 'anon';
  }
};

const isCacheableUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return CACHEABLE_PATHS.some((p) => url.includes(p));
};

const cacheKey = (url) => `${CACHE_PREFIX}${userScopeKey()}:${url}`;

const readCache = (url) => {
  try {
    const raw = localStorage.getItem(cacheKey(url));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.t !== 'number') return null;
    if (Date.now() - parsed.t > CACHE_TTL_MS) {
      localStorage.removeItem(cacheKey(url));
      return null;
    }
    return parsed.d;
  } catch {
    return null;
  }
};

const writeCache = (url, data) => {
  try {
    const json = JSON.stringify({ t: Date.now(), d: data });
    if (json.length > CACHE_MAX_BYTES) return; // skip oversize payloads
    localStorage.setItem(cacheKey(url), json);
  } catch (e) {
    // QuotaExceeded — best effort: clear our prefix and skip
    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const k = localStorage.key(i);
        if (k && k.startsWith(CACHE_PREFIX)) keys.push(k);
      }
      keys.forEach((k) => localStorage.removeItem(k));
    } catch { /* ignore */ }
  }
};

const isNonEmptyData = (data) => {
  if (data == null) return false;
  if (Array.isArray(data)) return data.length > 0;
  if (typeof data === 'object') {
    // Object with at least one own enumerable prop (excludes empty pagination wrappers)
    return Object.keys(data).length > 0;
  }
  return true;
};

// =================== INTERCEPTORS ===================

// Normalize FastAPI/Pydantic 422 error bodies into a plain string detail.
// Defends against React minified error #31 ("Objects are not valid as a
// React child") when components do `toast({ description: e.response.data.detail })`.
// The backend now also normalizes these centrally, but this client-side guard
// keeps the app safe even if a stale response somehow slips through.
const _normalizePydanticDetail = (data) => {
  if (!data || typeof data !== 'object') return data;
  const detail = data.detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (!d || typeof d !== 'object') return String(d ?? '');
        const loc = Array.isArray(d.loc) ? d.loc.slice(1).join('.') : '';
        const msg = d.msg || 'invalid value';
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean);
    return { ...data, detail: parts.join('; ') || 'Invalid request body' };
  }
  if (detail && typeof detail === 'object') {
    return { ...data, detail: detail.msg || JSON.stringify(detail) };
  }
  return data;
};

axios.interceptors.response.use(
  (response) => {
    // Persist successful GET reads
    try {
      const cfg = response.config || {};
      const method = (cfg.method || 'get').toLowerCase();
      if (method === 'get' && cfg.url && isCacheableUrl(cfg.url) && response.status >= 200 && response.status < 300) {
        // Only cache responses that actually have data — empty arrays/objects
        // for whitelist paths usually mean a transient backend issue, NOT
        // a genuine "nothing to show" state. We deliberately don't write
        // empty data to cache so we never poison the fallback.
        if (isNonEmptyData(response.data)) {
          writeCache(cfg.url, response.data);
        }
      }
    } catch { /* ignore cache write failures */ }
    return response;
  },
  async (error) => {
    const config = error.config;

    // Layer 0 — session invalidated by admin. Must run BEFORE the
    // Pydantic-detail normalizer below (which would stringify our
    // structured 401 payload into JSON, defeating this check). The
    // backend returns 401 with a payload of shape
    // `{detail: {code: 'session_invalidated', message: '...'}}` whenever
    // a token's iat < the current force-logout epoch. On seeing this
    // we:
    //   1. wipe the auth token + all our own IDB caches
    //   2. bust the browser cache (unregister SWs, clear CacheStorage)
    //   3. hard-navigate to /login so the next paint fetches a fresh
    //      index.html (pulling the latest fingerprinted JS/CSS).
    try {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const isSessionInvalid = (
        status === 401 &&
        typeof detail === 'object' &&
        detail !== null &&
        detail.code === 'session_invalidated'
      );
      if (isSessionInvalid && !window.__scb_session_invalidated_handled) {
        window.__scb_session_invalidated_handled = true;
        await bustAppCache();
        try {
          const { toast } = await import('sonner');
          toast.error('Session ended', {
            description:
              'An administrator ended all active sessions. Please log in again.',
            duration: 4000,
          });
        } catch { /* toast lib unavailable — silent fallback */ }
        setTimeout(() => {
          window.location.replace(`/login?bust=${Date.now()}`);
        }, 800);
      }
    } catch { /* never block the original rejection because of cleanup */ }

    // Normalize any non-string `detail` (Pydantic v2 422 array, dict, etc.)
    // BEFORE downstream catch handlers see it. Components can safely render
    // `error.response.data.detail` as a string from this point on.
    if (error?.response?.data) {
      try {
        error.response.data = _normalizePydanticDetail(error.response.data);
      } catch { /* ignore — fall through to existing flow */ }
    }

    // Layer 1 — retry idempotent reads on transient failures
    if (isRetryable(config, error)) {
      config._retryCount = (config._retryCount || 0) + 1;
      if (config._retryCount <= MAX_RETRIES) {
        const base = BACKOFF_MS[config._retryCount - 1] || 1500;
        const jitter = Math.floor(Math.random() * 250);
        await new Promise((res) => setTimeout(res, base + jitter));
        return axios(config);
      }
    }

    // Layer 2 — fall back to cache for cacheable GETs that exhausted retries
    try {
      if (config && config.url && isCacheableUrl(config.url)) {
        const method = (config.method || 'get').toLowerCase();
        if (method === 'get') {
          const cached = readCache(config.url);
          if (cached !== null && cached !== undefined) {
            // Return a synthetic 200 with cached data so the calling
            // component just sees "the request succeeded" — no empty state.
            return Promise.resolve({
              data: cached,
              status: 200,
              statusText: 'OK (from cache)',
              headers: { 'x-cache-fallback': '1' },
              config,
              request: null,
            });
          }
        }
      }
    } catch { /* ignore cache read failures */ }

    return Promise.reject(error);
  },
);

// =================== WARM-UP PING ===================
// Backend cold-starts are the single biggest cause of "everything is slow on
// first load" reports. Fire a no-op GET as soon as the app boots so by the
// time the user clicks something, the server is already warm. Best-effort —
// failures are completely silent (the user-triggered requests will still go
// through retry + cache fallback).
if (typeof window !== 'undefined') {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  setTimeout(() => {
    axios
      .get(`${baseUrl}/api/`, { _noRetry: true, timeout: 8000 })
      .catch(() => { /* silent — it's just a wake-up ping */ });
  }, 50);
}

export default axios;
